<template>
  <div class="space-y-6">
    <!-- Checklist Items -->
    <div class="bg-white shadow overflow-hidden sm:rounded-md">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          Profile Completeness Checklist
        </h3>

        <div class="space-y-4">
          <div
            v-for="item in checklistItems"
            :key="item.key"
            class="border-b border-gray-200 pb-4 last:border-b-0">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <label class="text-sm font-medium text-gray-700">
                  {{ item.label }}
                </label>
                <p
                  v-if="item.description"
                  class="text-sm text-gray-500 mt-1">
                  {{ item.description }}
                </p>
              </div>
              <div class="flex items-center space-x-4 ml-4">
                <label class="flex items-center cursor-pointer">
                  <input
                    :id="`${item.key}_yes`"
                    :name="item.key"
                    type="radio"
                    :value="true"
                    :checked="localItems[item.key] === true || localItems[item.key] === 'true'"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                    @change="setItemValue(item.key, true)">
                  <span class="ml-2 text-sm font-medium text-gray-700">Yes</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    :id="`${item.key}_no`"
                    :name="item.key"
                    type="radio"
                    :value="false"
                    :checked="localItems[item.key] === false || localItems[item.key] === 'false'"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                    @change="setItemValue(item.key, false)">
                  <span class="ml-2 text-sm font-medium text-gray-700">No</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Additional Information -->
    <div class="bg-white shadow overflow-hidden sm:rounded-md">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          Business Information
        </h3>

        <div class="space-y-4">
          <!-- Business Name -->
          <div>
            <label
              for="business_name"
              class="block text-sm font-medium text-gray-700">
              Business Name (as it appears on Google)
            </label>
            <input
              id="business_name"
              :value="businessName"
              type="text"
              class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              placeholder="Exact business name"
              @input="$emit('update:business-name', $event.target.value)">
          </div>

          <!-- Website URL -->
          <div>
            <label
              for="website_url"
              class="block text-sm font-medium text-gray-700">
              Website URL (on the profile)
            </label>
            <input
              id="website_url"
              :value="websiteUrl"
              type="url"
              class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              placeholder="https://example.com"
              @input="$emit('update:website-url', $event.target.value)">
          </div>

          <!-- Maps URL -->
          <div>
            <label
              for="maps_url"
              class="block text-sm font-medium text-gray-700">
              Google Maps URL
            </label>
            <input
              id="maps_url"
              :value="mapsUrl"
              type="url"
              class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              placeholder="https://maps.google.com/..."
              @input="$emit('update:maps-url', $event.target.value)">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },
  businessName: {
    type: String,
    default: ''
  },
  websiteUrl: {
    type: String,
    default: ''
  },
  mapsUrl: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'update:business-name', 'update:website-url', 'update:maps-url'])

// Checklist items configuration
const checklistItems = [
  {
    key: 'photos',
    label: 'Photos uploaded',
    description: 'Profile has sufficient photos (minimum 5-10 recommended)'
  },
  {
    key: 'address',
    label: 'Address verified',
    description: 'Address is accurate and properly formatted'
  },
  {
    key: 'hours',
    label: 'Regular hours set',
    description: 'Business hours are current and accurate'
  },
  {
    key: 'phone',
    label: 'Phone number added',
    description: 'Primary phone number is listed and correct'
  },
  {
    key: 'name',
    label: 'Business name correct',
    description: 'Name matches legal business name without keywords'
  },
  {
    key: 'reviews',
    label: 'Reviews present',
    description: 'Profile has customer reviews'
  },
  {
    key: 'category_selected',
    label: 'Category selected',
    description: 'Primary business category is set appropriately'
  },
  {
    key: 'website_linked',
    label: 'Website linked',
    description: 'Website URL is added to the profile'
  },
  {
    key: 'about',
    label: 'About section filled',
    description: 'Business description is complete and informative'
  }
]

// Local copy of items
const localItems = ref({ ...props.modelValue })

// Methods
const updateItems = () => {
  emit('update:modelValue', { ...localItems.value })
}

const setItemValue = (key, value) => {
  localItems.value[key] = value
  updateItems()
}

// Watch for external changes
watch(() => props.modelValue, (newValue) => {
  localItems.value = { ...newValue }
}, { deep: true })
</script>
